import { useState } from "react";
import { Overlay } from "../../../../styles/popup";
import {
  MissionCycle,
  MissionRewardType,
  MissionType,
} from "../../../../api/pub-mission";
import ProgressResetConfirmPopup from "../../../event/shared/progress-reset-confirm-popup";
import { useMissionForm } from "../../hooks/use-mission-form";
import * as S from "./styles";

interface MissionFormPopupProps {
  pubId: number;
  missionId?: number;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const PERIOD_INFO =
  "유저당 1회 참여, 반복 참여 불가. 시작일 09:00 ~ 종료일 다음날 09:00까지 진행. 종료 후 신규 진행도 처리 불가. 이미 달성한 보상은 종료 후에도 계속 수령 가능합니다.";

const ALWAYS_ON_INFO = `매장에서 직접 종료하기 전까지 반복됩니다.
· 일일: 해당일 09:00 ~ 다음날 09:00 (예: 8/14 → 8/14 09:00 ~ 8/15 09:00)
· 주간: 월요일 09:00 ~ 다음 주 월요일 09:00
· 월간: 매월 1일 09:00 ~ 다음 달 1일 09:00
· 시작일이 주기 중간이면 첫 주기는 시작일 09:00~해당 주기 종료 시점까지 진행됩니다.
· 유저는 매 주기마다 참여·보상 수령 가능합니다.
· 주기별로 여러 미션을 등록할 수 있습니다.`;

const MISSION_RESET_PHRASE = "미션 진행 상황을 초기화합니다";

const MissionFormPopup = ({
  pubId,
  missionId,
  onClose,
  onSuccess,
}: MissionFormPopupProps) => {
  const {
    form,
    isEdit,
    canEdit,
    loading,
    submitting,
    resetting,
    updateField,
    addReward,
    removeReward,
    updateReward,
    submit,
    resetProgress,
  } = useMissionForm({ pubId, missionId, onClose, onSuccess });

  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);

  const disabled = !canEdit || submitting;
  const hasPointReward = form.rewards.some(
    (reward) => reward.type === MissionRewardType.POINT,
  );

  const handleResetConfirm = async () => {
    try {
      await resetProgress();
      setIsResetConfirmOpen(false);
    } catch {
      // error already alerted in hook
    }
  };

  return (
    <Overlay $zIndex={1000}>
      <S.Popup>
        <S.Header>
          <div className="title">{isEdit ? "미션 수정" : "미션 등록"}</div>
          <img
            src="/image-web/tournament/close.svg"
            alt="close"
            onClick={onClose}
          />
        </S.Header>

        {loading ? (
          <S.LoadingMessage>미션 정보를 불러오는 중입니다.</S.LoadingMessage>
        ) : (
          <>
            <S.Body>
              {!canEdit && (
                <S.DisabledNotice>
                  <div className="message">
                    진행 중인 유저가 있어 미션을 수정할 수 없습니다.
                  </div>
                  <S.ResetProgressButton
                    type="button"
                    disabled={resetting}
                    onClick={() => setIsResetConfirmOpen(true)}
                  >
                    진행 초기화
                  </S.ResetProgressButton>
                </S.DisabledNotice>
              )}

              <S.Section>
                <S.SectionTitle>기본 정보</S.SectionTitle>

                <S.Field>
                  <S.Label>
                    미션명 <span className="required">*</span>
                  </S.Label>
                  <S.TextInput
                    placeholder="미션명을 입력하세요"
                    value={form.name}
                    disabled={disabled}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </S.Field>

                <S.Field>
                  <S.Label>미션 설명</S.Label>
                  <S.TextArea
                    placeholder="미션 설명을 입력하세요"
                    value={form.description}
                    disabled={disabled}
                    onChange={(e) => updateField("description", e.target.value)}
                  />
                </S.Field>

                <S.Field>
                  <S.Label>
                    운영 방식 <span className="required">*</span>
                  </S.Label>
                  <S.RadioGroup>
                    <S.RadioOption>
                      <input
                        type="radio"
                        checked={!form.isAlwaysOn}
                        disabled={disabled}
                        onChange={() => updateField("isAlwaysOn", false)}
                      />
                      기간 미션
                    </S.RadioOption>
                    <S.RadioOption>
                      <input
                        type="radio"
                        checked={form.isAlwaysOn}
                        disabled={disabled}
                        onChange={() => updateField("isAlwaysOn", true)}
                      />
                      상시 미션
                    </S.RadioOption>
                  </S.RadioGroup>
                </S.Field>

                <S.DateRow>
                  <S.Field>
                    <S.Label>
                      시작일 <span className="required">*</span>
                    </S.Label>
                    <S.TextInput
                      type="date"
                      value={form.startDate}
                      disabled={disabled}
                      onChange={(e) => updateField("startDate", e.target.value)}
                    />
                    <S.SubText>시작일 09:00부터 진행됩니다</S.SubText>
                  </S.Field>

                  {!form.isAlwaysOn ? (
                    <S.Field>
                      <S.Label>
                        종료일 <span className="required">*</span>
                      </S.Label>
                      <S.TextInput
                        type="date"
                        value={form.endDate}
                        disabled={disabled}
                        onChange={(e) => updateField("endDate", e.target.value)}
                      />
                      <S.SubText>종료일 다음날 09:00까지 진행됩니다</S.SubText>
                    </S.Field>
                  ) : (
                    <S.Field>
                      <S.Label>
                        반복 주기 <span className="required">*</span>
                      </S.Label>
                      <S.RadioGroup>
                        <S.RadioOption>
                          <input
                            type="radio"
                            checked={form.missionCycle === MissionCycle.DAILY}
                            disabled={disabled}
                            onChange={() =>
                              updateField("missionCycle", MissionCycle.DAILY)
                            }
                          />
                          일일
                        </S.RadioOption>
                        <S.RadioOption>
                          <input
                            type="radio"
                            checked={form.missionCycle === MissionCycle.WEEKLY}
                            disabled={disabled}
                            onChange={() =>
                              updateField("missionCycle", MissionCycle.WEEKLY)
                            }
                          />
                          주간
                        </S.RadioOption>
                        <S.RadioOption>
                          <input
                            type="radio"
                            checked={form.missionCycle === MissionCycle.MONTHLY}
                            disabled={disabled}
                            onChange={() =>
                              updateField("missionCycle", MissionCycle.MONTHLY)
                            }
                          />
                          월간
                        </S.RadioOption>
                      </S.RadioGroup>
                    </S.Field>
                  )}
                </S.DateRow>

                <S.InfoBox>
                  {form.isAlwaysOn ? ALWAYS_ON_INFO : PERIOD_INFO}
                </S.InfoBox>
              </S.Section>

              <S.Section>
                <S.SectionTitle>진행 방식</S.SectionTitle>

                <S.RadioGroup>
                  <S.ProgressOption>
                    <S.RadioOption>
                      <input
                        type="radio"
                        checked={form.type === MissionType.ONE_OFF}
                        disabled={disabled}
                        onChange={() => updateField("type", MissionType.ONE_OFF)}
                      />
                      완료형
                    </S.RadioOption>
                    <S.ProgressSubText>
                      미션이 한 번에 완료 처리됩니다.
                    </S.ProgressSubText>
                  </S.ProgressOption>

                  <S.ProgressOption>
                    <S.RadioOption>
                      <input
                        type="radio"
                        checked={form.type === MissionType.CUMULATIVE}
                        disabled={disabled}
                        onChange={() =>
                          updateField("type", MissionType.CUMULATIVE)
                        }
                      />
                      누적형
                    </S.RadioOption>
                  </S.ProgressOption>
                </S.RadioGroup>

                {form.type === MissionType.CUMULATIVE && (
                  <S.Field>
                    <S.Label>
                      목표 횟수 <span className="required">*</span>
                    </S.Label>
                    <S.TextInput
                      type="number"
                      min={1}
                      value={form.count}
                      disabled={disabled}
                      onChange={(e) => updateField("count", e.target.value)}
                    />
                    <S.SubText>
                      매장에서 직접 진행도를 올리거나 내리며, 목표 횟수에
                      도달하면 완료됩니다.
                    </S.SubText>
                  </S.Field>
                )}

                <S.WarningBox>
                  ⚠️ 모든 진행 조건은 수동으로 처리해야합니다. 외부 데이터 자동
                  판정 없음.
                </S.WarningBox>
              </S.Section>

              <S.Section>
                <S.RewardHeader>
                  <S.SectionTitle>보상 설정</S.SectionTitle>
                  <S.AddRewardButton
                    type="button"
                    disabled={disabled}
                    onClick={addReward}
                  >
                    + 보상 추가
                  </S.AddRewardButton>
                </S.RewardHeader>

                <S.RewardList>
                  {form.rewards.map((reward, index) => (
                    <S.RewardCard key={reward.key}>
                      <S.RewardCardHeader>
                        <div className="left">
                          <S.RewardBadge>보상 {index + 1}</S.RewardBadge>
                        </div>
                        {form.rewards.length > 1 && (
                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() => removeReward(reward.key)}
                          >
                            삭제
                          </button>
                        )}
                      </S.RewardCardHeader>

                      <S.RewardFieldRow>
                        <S.Label>보상 유형</S.Label>
                        <S.RadioGroup>
                          <S.RadioOption>
                            <input
                              type="radio"
                              checked={reward.type === MissionRewardType.POINT}
                              disabled={
                                disabled ||
                                (hasPointReward &&
                                  reward.type !== MissionRewardType.POINT)
                              }
                              onChange={() =>
                                updateReward(reward.key, {
                                  type: MissionRewardType.POINT,
                                  count: reward.count ?? 1,
                                  name: undefined,
                                })
                              }
                            />
                            매장이용권
                          </S.RadioOption>
                          <S.RadioOption>
                            <input
                              type="radio"
                              checked={reward.type === MissionRewardType.OTHER}
                              disabled={disabled}
                              onChange={() =>
                                updateReward(reward.key, {
                                  type: MissionRewardType.OTHER,
                                  name: reward.name ?? "",
                                  count: undefined,
                                })
                              }
                            />
                            기타 보상
                          </S.RadioOption>
                        </S.RadioGroup>
                      </S.RewardFieldRow>

                      {reward.type === MissionRewardType.POINT ? (
                        <S.RewardFieldRow>
                          <S.Label>지급 수량</S.Label>
                          <S.TextInput
                            type="number"
                            min={1}
                            value={reward.count ?? ""}
                            disabled={disabled}
                            onChange={(e) =>
                              updateReward(reward.key, {
                                count: Number(e.target.value),
                              })
                            }
                          />
                          <S.SubText>
                            유저 앱에서 &apos;보상받기&apos;를 누르면 자동
                            지급됩니다.
                          </S.SubText>
                        </S.RewardFieldRow>
                      ) : (
                        <S.RewardFieldRow>
                          <S.Label>보상명</S.Label>
                          <S.TextInput
                            placeholder="보상명을 입력하세요"
                            value={reward.name ?? ""}
                            disabled={disabled}
                            onChange={(e) =>
                              updateReward(reward.key, { name: e.target.value })
                            }
                          />
                        </S.RewardFieldRow>
                      )}
                    </S.RewardCard>
                  ))}
                </S.RewardList>
              </S.Section>
            </S.Body>

            <S.Footer>
              <S.CancelButton type="button" onClick={onClose}>
                취소
              </S.CancelButton>
              <S.SubmitButton
                type="button"
                disabled={disabled || submitting}
                onClick={submit}
              >
                {isEdit ? "수정" : "등록"}
              </S.SubmitButton>
            </S.Footer>
          </>
        )}
      </S.Popup>

      {isResetConfirmOpen && (
        <ProgressResetConfirmPopup
          title="진행 초기화"
          description="모든 유저의 미션 진행 상황이 초기화되며, 지급된 보상도 취소·회수될 수 있습니다."
          confirmPhrase={MISSION_RESET_PHRASE}
          submitting={resetting}
          onClose={() => {
            if (!resetting) setIsResetConfirmOpen(false);
          }}
          onConfirm={handleResetConfirm}
        />
      )}
    </Overlay>
  );
};

export default MissionFormPopup;
